import {FC} from 'react'
import {LaborProposalAspectComponentModel, LaborProposalAspectModel} from '../../core/_models'

/**
 * How the recommendation compares, concern by concern.
 *
 * "2.6% better than the current schedule" is one number over four different
 * things, and the first week generated against real availability data showed why
 * that is not enough: it cleared every one of the current schedule's five
 * availability breaches AND gave up 18.5 hours of coverage to do it. The single
 * badge describes neither half of that trade, and the trade is the decision.
 *
 * Every metric from the API is a DEFECT count (lower is better), so a card can
 * only say "removes N" or "adds N" — never a vague "improved".
 */

const DIRECTION_STYLES: Record<string, {badge: string; border: string; text: string}> = {
  better: {badge: 'bg-success', border: 'border-success', text: 'text-success'},
  worse: {badge: 'bg-danger', border: 'border-danger', text: 'text-danger'},
  // The grey badges use the theme's gray scale, which flips with the colour
  // mode. Bootstrap's bg-light/bg-secondary do not, so a "not measured" pill
  // came out dark-on-dark in dark mode and pale-on-pale in light mode.
  same: {badge: 'bg-gray-300 text-gray-800', border: 'border-gray-300', text: 'text-gray-600'},
  unknown: {badge: 'bg-gray-200 text-gray-700', border: 'border-gray-300', text: 'text-gray-500'},
}

const styleFor = (direction: string) => DIRECTION_STYLES[direction] || DIRECTION_STYLES.unknown

const round1 = (value: number) => Math.round(value * 10) / 10

/** '3 hours' / '1 violation' — the unit only reads right when it agrees. */
const withUnit = (value: number, unit: string) => {
  const label = value === 1 && unit.endsWith('s') ? unit.slice(0, -1) : unit
  return `${round1(value)} ${label}`
}

/**
 * The badge: the change in the DEFECT ITSELF — "37% less overtime", not a share
 * of anything else.
 *
 * This is the number the body line's arithmetic produces (12.5 fewer of 33.5),
 * which is the point: the badge used to read "+37.3%" beside "96.6% fit vs
 * 94.7%" — a 37% cut in overtime hours next to a 1.9-point move in overtime as a
 * share of all scheduled hours. Two correct numbers on two different bases, with
 * nothing on the card admitting it, which just looks wrong.
 *
 * A percentage of zero defects is not a number, so when the current schedule has
 * nothing wrong in an aspect the badge states what was given up in units instead
 * of inventing a figure.
 */
const headline = (aspect: LaborProposalAspectModel): string => {
  if (aspect.direction === 'unknown') return 'not measured'
  if (aspect.direction === 'same') return 'no change'
  if (aspect.improvement_pct !== null) {
    return `${Math.abs(aspect.improvement_pct)}% ${aspect.improvement_pct > 0 ? 'less' : 'more'}`
  }
  if (aspect.delta === null) return aspect.direction
  // Words, not a signed number. These are DEFECT counts, so "+37" on a red card
  // would be read as a gain by anybody who has ever seen a dashboard.
  return `${withUnit(Math.abs(aspect.delta), aspect.unit)} ${aspect.direction}`
}

/**
 * The primary line: the two counts and the difference between them, so the
 * badge's percentage is visibly that difference over the current value rather
 * than a figure the reader has to trust.
 */
const changeLine = (aspect: LaborProposalAspectModel): string => {
  if (aspect.proposal === null && aspect.actual === null) {
    return 'not measured for this recommendation'
  }
  const recommended = aspect.proposal === null ? '—' : round1(aspect.proposal)
  const current = aspect.actual === null ? '—' : round1(aspect.actual)
  const base = `${recommended} vs ${current} ${aspect.unit} now`
  if (!aspect.delta) return base
  const size = round1(Math.abs(aspect.delta))
  return `${base} · ${size} ${aspect.delta > 0 ? 'fewer' : 'more'}`
}

/**
 * The fit line, with its base spelled out.
 *
 * "96.6%" alone invites the reader to compare it against the badge, which is a
 * different quantity. "96.6% of scheduled hours inside the overtime threshold"
 * cannot be mistaken for one.
 */
const fitLine = (aspect: LaborProposalAspectModel): string | null => {
  if (aspect.proposal_fitness_pct === null) return null
  const of = aspect.fitness_label ? ` ${aspect.fitness_label}` : ' fit'
  const was = aspect.actual_fitness_pct === null ? '' : ` (was ${aspect.actual_fitness_pct}%)`
  return `${aspect.proposal_fitness_pct}%${of}${was}`
}

/**
 * The parts worth showing on the card: the ones that MOVED.
 *
 * A part that is identical on both sides ("hard-minimum slots unstaffed: 0 vs 0")
 * is reassurance, not information, and four of those per card would bury the one
 * line that matters — a part regressing while its aspect improves, which is
 * exactly what an aspect total hides. Capped at three so a card stays a card;
 * the full list is in the title tooltip.
 */
const notableComponents = (aspect: LaborProposalAspectModel): LaborProposalAspectComponentModel[] =>
  aspect.components.filter((c) => c.delta !== null && c.delta !== 0).slice(0, 3)

const componentTooltip = (aspect: LaborProposalAspectModel): string | undefined => {
  if (!aspect.components.length) return undefined
  return aspect.components
    .map((c) => {
      const recommended = c.proposal === null ? '—' : round1(c.proposal)
      const current = c.actual === null ? '—' : round1(c.actual)
      return `${c.label}: ${recommended} recommended vs ${current} now`
    })
    .join('\n')
}

const ComponentRow: FC<{
  component: LaborProposalAspectComponentModel
  aspectDirection: string
}> = ({component, aspectDirection}) => {
  // Only a part pulling AGAINST the aspect is coloured. Colouring the agreeing
  // ones too would make every card a wall of green and hide the exception.
  const contradicts =
    component.direction !== aspectDirection &&
    (component.direction === 'better' || component.direction === 'worse')
  const style = contradicts ? styleFor(component.direction).text : 'text-gray-600'
  const recommended = component.proposal === null ? '—' : round1(component.proposal)
  const current = component.actual === null ? '—' : round1(component.actual)
  return (
    <div className={`fs-9 ${style}`}>
      {contradicts ? '⚠ ' : '· '}
      {component.label}: <span className='fw-semibold'>{recommended}</span> vs {current}
    </div>
  )
}

const AspectCard: FC<{aspect: LaborProposalAspectModel}> = ({aspect}) => {
  const style = styleFor(aspect.direction)
  const fit = fitLine(aspect)
  return (
    <div className='col-6'>
      <div
        className={`border ${style.border} border-dashed rounded p-3 h-100`}
        title={componentTooltip(aspect)}
      >
        <div className='d-flex align-items-center justify-content-between gap-2'>
          <span className='fs-8 text-uppercase fw-bold text-gray-600'>{aspect.label}</span>
          <span className={`badge ${style.badge} fs-9 flex-shrink-0`}>{headline(aspect)}</span>
        </div>
        {/* The counts first, because the badge is a percentage OF them. */}
        <div className='fs-7 fw-bolder mt-1'>{changeLine(aspect)}</div>
        {fit && <div className='fs-9 text-gray-600'>{fit}</div>}
        {notableComponents(aspect).map((component) => (
          <ComponentRow
            key={component.field}
            component={component}
            aspectDirection={aspect.direction}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Renders nothing when the recommendation predates the split — an empty section
 * header over four "not measured" cards tells a manager less than no section.
 */
const ProposalAspects: FC<{aspects: LaborProposalAspectModel[] | undefined}> = ({aspects}) => {
  const measured = (aspects || []).filter((a) => a.proposal !== null || a.actual !== null)
  if (!measured.length) return null

  return (
    <div className='mb-6'>
      <label className='form-label fw-bold'>How it compares, aspect by aspect:</label>
      <div className='row g-3'>
        {measured.map((aspect) => (
          <AspectCard key={aspect.key} aspect={aspect} />
        ))}
      </div>
      <div className='text-gray-500 fs-9 mt-2'>
        Each badge is the change in that concern itself (e.g. 12.5 of 33.5 overtime hours = 37%
        less); the grey line underneath measures the same week against a different base, and says
        which. A recommendation can be better on one concern and worse on another — ⚠ marks a part
        pulling against its own heading.
      </div>
    </div>
  )
}

export {ProposalAspects}
